import { PrescriptionPadStep } from '@/components/PatientPageV2/components/PrescriptionPad';
import { ConsultationTabOverviewTable } from '@/components/PatientPageV2/ConsultationTabOverviewTable';
import { useConsultationTabOverviewTable } from '@/components/PatientPageV2/hooks/useConsultationTabOverviewTable';
import {
  FF_BOOK_ANY_DOCTOR_IF_CURRENT_DOCTOR_INACTIVE,
  FF_C4C_IMMEDIATE_NURSE_CONSULTATION_V2,
  FF_CONSULTATION_CHARGES_UPLIFT,
  FF_ENABLE_INITIAL_DR_PAYMENT,
  FF_NURSE_INSTANT_CONSULTATION,
  FF_UNSAVED_CONSULTATION_CHANGES_PROMPT,
} from '@/constants/featureFlags';
import settings from '@/data/constants';
import { useFeatureFlags } from '@/hooks';
import { useGetConsultationStatus } from '@/hooks/rest/useGetConsultationStatus';
import { SectionHeader } from '@/shared-ui/PageElements/SectionHeader';
import { Button, ConfirmDialog, TextField, toast } from '@montugroup/design-system';
import { triggerSourcePatientTelecomAttributeEnum } from '@montugroup/pms-api-contracts';
import { Box, debounce, Stack } from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { listSorter } from '@/assets/js/util';
import { getAuthData, getUser } from '@/data/service/authService';
import { makePOST } from '@/data/service/dataService';
import { useAppStore } from '@/state-management';
import useConsult from '../../hooks/consult/useConsult';
import SelectField from '../fields/SelectField';

import type { TypedAnalyticsContexts } from '@/analytics/snowplow/analytics-context.types';
import { AnalyticsTrackingProvider } from '@/analytics/snowplow/AnalyticsTrackingContext';
import type { CreateInstantConsultationDto } from '@/api/consultations/createInstantConsultation';
import { ConsultType } from '@/components/PatientPageV2/utils/getConsultationType';
import { useChargeInitialDoctorConsult } from '@/hooks/consult/useChargeInitialDoctorConsult';
import { useConsultActions } from '@/hooks/consult/useConsultActions';
import { useConsultLoggingInfo } from '@/hooks/consult/useConsultLoggingInfo';
import { useCreateInstantConsultation } from '@/hooks/consult/useCreateInstantConsultation';
import type { SaveConsultationFunc } from '@/hooks/consult/useSaveConsultation';
import type { SaveConsultationParams } from '@/hooks/consult/useSaveConsultationData';
import { useGetAustralianStates } from '@/hooks/patients/useGetAustralianStates';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import { usePatientNotes } from '@/hooks/patients/usePatientNotes';
import { usePatientPrescriptions } from '@/hooks/patients/usePatientPrescriptions';
import { useSavePatientTelecomAttributes } from '@/hooks/telecom/useSavePatientTelecomAttributes';
import useDebouncedCallback from '@/hooks/useDebouncedCallback';
import type { OnSaveConsultationOptions } from '@/pages/Patient';
import type { ConsultationSubStatusType, PatientModel, ResponseCursors } from '@/types';
import { Logger } from '@montu-web/utilities';
import { ConsultationLinkCard, TelecomService, useAmazonConnectCcp } from '@montugroup/clinic-telecom';
import type { PatientConsultationSummaryDto } from '@montugroup/pms-api-contracts';
import { useModal } from 'mui-modal-provider';
import type { PrescriptionAssistantToolDetails } from '../Patient/components/PrescriptionAssistantTool/PrescriptionAssistantTool.types';
import { PatientContext } from '../Patient/PatientContext';
import { useScheduledConsultId } from '../Patient/ScheduledConsult/hooks/useScheduledConsultId';
import { BookConsultationMenu } from './components/BookConsultationMenu/BookConsultationMenu';
import { CompleteConsultationModal } from './components/ConsultationTabSections/CompleteConsultationModal';
import { PatientNotes } from './components/ConsultationTabSections/PatientNotes';
import { ShowConsultation } from './components/ConsultationTabSections/ShowConsultation';
import ViewPatientNote from './components/ConsultationTabSections/ViewPatientNote';
import type { PaymentButtonText } from './ConsultationTab.types';
import { useAutoSpotConsultationCreationDialog } from './hooks/useAutoSpotConsultationCreationDialog';
import { useConsultationLink } from './hooks/useConsultationLink';
import { useConsultationTabState } from './hooks/useConsultationTabState';
import { getConsultationType } from './utils/getConsultationType';

const logger = new Logger('PMS:ConsultationTab');

type ConsultationTabProps = {
  // TODO: we should be using  `Consultation` type here instead of `any`
  showConsultation?: any;
  setShowConsultation: React.Dispatch<React.SetStateAction<any>>;
  data?: {
    consultations: Consultation[];
    patientNotes?: PatientModel['Notes'];
  };
  fields: any;
  doctor?: any;
  onSaveConsultation: (params?: OnSaveConsultationOptions) => Promise<void>;
  patientId: number;
  saveConsultation: SaveConsultationFunc;
  submitNurseConsultation: (consultationData: SaveConsultationParams | null) => Promise<void>;
  consultationStarted?: boolean;
  setConsultationStarted: (consultationStarted: boolean) => void;
  setConsultationSubStatus: any;
  refresh?: any;
  patient?: any;
  onCreditCardHasBeenCharged?: any;
  onInvoiceHasBeenCreated?: any;
  doctorsList?: any;
  handleChange?: any;
  responseCursors?: ResponseCursors;
  handlePaginationChange?: any;
  isLoadingConsultations?: boolean;
  isErrorConsultations?: boolean;
  consultationSubStatus?: any;
  consultationSummary?: PatientConsultationSummaryDto;
  setConsultationsSearch?: Dispatch<SetStateAction<string>>;
  setConsultationsCursors?: Dispatch<SetStateAction<ResponseCursors>>;
  onSort?: (
    sort: {
      id: string;
      desc: boolean;
    }[],
  ) => void;
  onPrescriptionErrorClosed: () => void;
  prescriptionDetails: PrescriptionAssistantToolDetails;
};

const ConsultationTab = (props: ConsultationTabProps) => {
  const { showModal } = useModal();
  const { flags } = useFeatureFlags();
  const tabCloseListenerRef = useRef(false);
  const ffUnsavedConsultationChanges = flags[FF_UNSAVED_CONSULTATION_CHANGES_PROMPT];
  const ffConsultationChargesUplift = flags[FF_CONSULTATION_CHARGES_UPLIFT];
  const ffBookAnyDoctorIfDoctorIsDisabled = flags[FF_BOOK_ANY_DOCTOR_IF_CURRENT_DOCTOR_INACTIVE];
  const ffNurseInstantConsultationCreationEnabled = flags[FF_NURSE_INSTANT_CONSULTATION];
  const ffIsC4CImmediateNurseConsultationV2Enabled = flags[FF_C4C_IMMEDIATE_NURSE_CONSULTATION_V2];
  const ffEnableInitialDrPayment = flags[FF_ENABLE_INITIAL_DR_PAYMENT];
  const { refetchPrescriptions } = usePatientPrescriptions(props.patientId);
  const {
    showConsultation,
    consultationSubStatus,
    setConsultationSubStatus,
    setShowConsultation,
    onCreditCardHasBeenCharged,
    onInvoiceHasBeenCreated,
    saveConsultation,
  } = props;
  const consultationData: Consultation[] =
    props.data && props.data.consultations ? listSorter(props.data.consultations, 'start_time') : [];
  const refreshConsultationTab = props.refresh;
  const { email, FirstName, LastName, phone, redFlag, redFlagComment } = useContext(PatientContext);
  const {
    patientNotesData,
    responseNotesCursors,
    onSort,
    refetchNotes,
    handleNotesPaginationChange,
    setNotesCursors,
    setNotesSearch,
    isPatientNotesError,
  } = usePatientNotes(props.patientId);
  const notes: PatientModel['Notes'] = patientNotesData ?? [];

  const initialCompletedNurseConsults: boolean = Boolean(props.consultationSummary?.hasCompletedInitialNurseConsult);

  const scheduledDoctorConsults: boolean = Boolean(props.consultationSummary?.hasScheduledDoctorConsult);

  const initialDoctorCompletedConsults: boolean = Boolean(props.consultationSummary?.hasCompletedInitialDoctorConsult);

  const consultsWithDoctor: boolean = Boolean(props.consultationSummary?.hasDoctorConsults);

  const authData = getAuthData();
  const { isAdminOnly, isAdmin, isDoc } = authData;

  const [selectedPatientNote, setSelectedPatientNote] = React.useState(null);
  const patientActive = props.fields?.isActive;

  const [followUpConsultationWeeks, setFollowUpConsultationWeeks] = useState(8);

  const { submitPrescriptions, isPostingPrescription, prescribeStateError } = useConsultationTabState({
    doctorId: Number(props?.doctor?.id),
    patientId: Number(props.patientId),
    showConsultation,
  });

  const { consultationStatuses } = useGetConsultationStatus(showConsultation);

  const user = getUser();

  const { consultationLinkProps } = useConsultationLink({
    consultations: consultationData,
    consultationStarted: props.consultationStarted,
    setConsultationStarted: props.setConsultationStarted,
    showConsultation: showConsultation,
    setShowConsultation: setShowConsultation,
    patientId: props.patientId,
    patientName: `${props.patient.first_name} ${props.patient.last_name}`,
    patientCode: props.patient?.patient_code,
    patientPhoneNumber: props.patient.phone,
  });

  const {
    prescriptionPad: { step: createScriptStep },
  } = useAppStore.use.prescriptionAssistant();

  const updatePrescriptionPadStep = useAppStore.use.updatePrescriptionPadStep();
  const allowAnyDocBooking = ffBookAnyDoctorIfDoctorIsDisabled && props.doctor?.DoctorUser?.active === false;
  const enableBookAnyDoctorButton: boolean =
    !isDoc && initialCompletedNurseConsults && (!consultsWithDoctor || allowAnyDocBooking);
  const enableBookSpecificDoctorButton: boolean = !isDoc && initialCompletedNurseConsults && !scheduledDoctorConsults;
  const initialDoctorConsultCompleted: boolean =
    !isDoc && initialCompletedNurseConsults && initialDoctorCompletedConsults;

  const currentConsultPaymentDetails = showConsultation?.paymentStatus || {};

  const { paymentMethod: consultPaymentMethod, paymentState: consultPaymentState } = useConsult({
    paymentStatus: showConsultation?.payment_status,
    paymentDetails: currentConsultPaymentDetails,
    paidDate: showConsultation?.paid_date ?? '',
    paymentQueued: showConsultation?.payment_queued,
    invoiceCode: showConsultation?.xero_consultation_code,
  });
  const isConcessionPatient = Boolean(props.patient?.hasValidConcessionCard);

  const consultAndClinicianType = getConsultationType({
    consult_type: showConsultation?.consult_type,
    doctor_id: showConsultation?.doctor_id,
  });

  const { shouldNotChargeInitialDoctorConsultation } = useChargeInitialDoctorConsult(
    showConsultation?.consult_type,
    showConsultation?.doctor_id,
    showConsultation?.hasPaidInitialNurseConsult,
  );

  const refetchPrescriptionData = () => {
    refetchPrescriptions();
  };

  const onPrescriptionActionComplete = (emptyPrescription: boolean) => {
    if (!emptyPrescription) {
      refetchPrescriptionData();
    }

    setShowConsultation(null);
  };

  const [isComplimentary, setIsComplimentary] = useState(false);

  const {
    loadingAnyPayment,
    loadingConsultCompletion,
    consultDiscountRate,
    handleChangeConsultDiscountRate,
    isCompleteConsultationModalOpen,
    handleCloseCompleteConsultationModal,
    handleShopifyPaymentRequest,
    handleConcessionPayment,
    handleConsultPayment,
    handleClearPrescription,
    handleNoShow,
    handleCompleteConsultation,
    handleConfirmCompleteConsult,
    onOnlyCancelComplete,
  } = useConsultActions({
    consult: showConsultation,
    setConsult: setShowConsultation,
    isConcessionPatient,
    patientEmail: email,
    patientActive: patientActive ?? false,
    onCreditCardHasBeenCharged,
    onInvoiceHasBeenCreated,
    saveConsult: saveConsultation,
    submitCreatePrescription: submitPrescriptions,
    onPrescriptionActionComplete,
    isComplimentary,
    shouldNotChargeInitialDoctorConsultation,
    nextConsultationInWeeks: followUpConsultationWeeks,
  });

  const getConsultLoggingInfo = useConsultLoggingInfo({ consult: showConsultation, isConcessionPatient });
  const handleIsComplimentaryChange = () => {
    logger.info('setIsComplimentary', getConsultLoggingInfo({ isComplimentary: !isComplimentary }));
    setIsComplimentary((prev) => !prev);
  };

  const onClearPrescription = () => {
    handleClearPrescription();
    refetchPrescriptionData();

    setFollowUpConsultationWeeks(8);
  };

  const onNoShow = () => {
    handleNoShow();
    props.refresh();
    setShowConsultation(null);
  };

  const currentConsultation = useMemo(() => {
    if (!showConsultation || !consultationData) {
      return null;
    }
    return consultationData.find((consultation: Consultation) => consultation.id === showConsultation.id);
  }, [consultationData, showConsultation]);

  const checkHaveNotesChanged = useCallback(() => {
    if (!showConsultation?.notes && !currentConsultation?.notes) {
      return false;
    }

    return showConsultation?.notes !== currentConsultation?.notes;
  }, [currentConsultation, showConsultation]);

  useEffect(() => {
    if (tabCloseListenerRef.current) {
      return;
    }

    const handleBeforeUnload = (event: { preventDefault: () => void; returnValue: string }) => {
      if (ffUnsavedConsultationChanges && checkHaveNotesChanged()) {
        event.preventDefault();
        event.returnValue = ''; // It does not look like we can control text on browser default alert.
      }
    };

    tabCloseListenerRef.current = true;
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      tabCloseListenerRef.current = false;
    };
  }, [tabCloseListenerRef, checkHaveNotesChanged, ffUnsavedConsultationChanges]);

  useEffect(() => {
    if (ffEnableInitialDrPayment) {
      return;
    }

    if (!currentConsultation || !showConsultation || currentConsultation.id !== showConsultation.id) {
      return;
    }

    // Only update if the pricing tier has changed within a consultation
    if (currentConsultation.pricingTier?.id !== showConsultation.pricingTier?.id) {
      setShowConsultation((prevShowConsultation: Consultation) => ({
        ...currentConsultation,
        notes: prevShowConsultation.notes,
        pricingTier: currentConsultation.pricingTier,
      }));
    }
  }, [currentConsultation, showConsultation?.pricingTier?.id]);

  const scheduledConsultId = useScheduledConsultId();

  const { consultationsOverviewTableData, isProcessingConsultations } = useConsultationTabOverviewTable({
    consultations: consultationData,
    patientEmail: email,
    setShowConsultation: setShowConsultation,
    scheduledConsultId: scheduledConsultId,
    setConsultationSubStatus: setConsultationSubStatus,
    handleRefetchPatientData: props.refresh,
  });

  const [searchFilterText, setSearchFilterText] = useState('');

  const handleSearchOnChange = (event: {
    target: {
      value: React.SetStateAction<string>;
    };
  }) => {
    const searchValue = event.target.value;
    setSearchFilterText(searchValue);
    props.setConsultationsCursors?.({ prev: null, next: null });
    props.setConsultationsSearch?.(searchValue);
  };

  function getPaymentButtonText(): PaymentButtonText {
    if (consultPaymentState === 'PAID') {
      return 'Paid - Resend Receipt';
    }
    if (consultPaymentState === 'IN_PROGRESS' && consultPaymentMethod === 'CREDIT_CARD') {
      return 'Payment requested';
    }
    if (consultPaymentState === 'IN_PROGRESS' && consultPaymentMethod === 'INVOICE') {
      return isDoc ? 'Resend Request' : 'Resend Invoice';
    }
    if (consultAndClinicianType === ConsultType.FollowUpDoctor) {
      return 'Charge for Dr Follow up appt';
    }
    if (consultAndClinicianType === ConsultType.InitialNurse) {
      return ffConsultationChargesUplift ? 'Payment Request' : 'Charge for Nurse Initial appt';
    }
    return 'N/A';
  }

  // Disable the button when credit card payment is in progress since that is an automated payment system, however
  // enable for invoice in progress method to enable clinicians to resend invoices.
  const isPaymentButtonDisabled =
    loadingAnyPayment || (consultPaymentMethod === 'CREDIT_CARD' && consultPaymentState === 'IN_PROGRESS');

  // Always provide clinicians the option to charge at the concession rate, not just when the patient is flagged as
  // a concession holder in the system. This allows us to support scenarios whereby the clinician is still waiting for
  // the patient to email their card details to them. We don't want to disrupt the clinicians workflow over a singular
  // consultation discount. This will change in phase 2 of the concession feature.
  const statusIsNotPaid = consultPaymentState === 'NOT_PAID';
  const canShowDiscountRateInput = statusIsNotPaid;

  const { data: ausStates } = useGetAustralianStates();
  const isPatientFromTasmania = (stateId?: number): boolean | undefined => {
    return ausStates?.some(({ id, name }) => id === stateId && name === 'TAS');
  };

  const nurseOnSave = () => {
    if (!patientActive) {
      return;
    }

    const consultData: SaveConsultationParams = {
      id: showConsultation.id,
      notes: showConsultation.notes,
      status_id: showConsultation.status_id,
      sub_status_id:
        consultationSubStatus?.id && showConsultation.status_id === settings.consultationStatus.ineligible
          ? consultationSubStatus?.id
          : null,
    };
    props.submitNurseConsultation(consultData);
  };

  const handleSendLateMail = async () => {
    const response = await makePOST(
      `consultation/mail/running-late?patient_id=${props.patientId}`,
      //@ts-expect-error: TS2345 - need to fix this type error
      {},
      'runningLate-ConsultationTab',
    );
    if (response) {
      toast.success('Running late email sent successfully');
    } else {
      toast.error('Failed sending running late email');
    }
  };

  const {
    mutateAsync: createSpotConsultation,
    isSuccess: createInstantConsultationSuccess,
    isError: createInstantConsultationError,
  } = useCreateInstantConsultation();

  const [createdConsultation, setCreatedConsultation] = useState<CreateInstantConsultationDto>();

  useEffect(() => {
    if (createInstantConsultationError) {
      toast.error('Failed to create spot consultation');
      return;
    }

    const isConsultationCreated = createdConsultation;
    if (createInstantConsultationSuccess && isConsultationCreated) {
      const consultation = createdConsultation?.attributes;
      if (consultation) {
        setShowConsultation({
          id: consultation.id,
          consultation_code: consultation.code,
          end_time: consultation.startTime,
          start_time: consultation.endTime,
          doctor_id: consultation.doctor?.id,
          payment_status: consultation.paymentStatus,
          paid_date: consultation.paidDate,
          consult_type: consultation.type,
          notes: consultation.notes,
          zoom_url: consultation.zoomUrl,
          status_id: consultation.consultationStatus?.id,
          sub_status_id: consultation.consultationSubStatus?.id,
          payment_queued: consultation.paymentQueued,
          calendly_consultation_uuid: null,
          calendly_edit_uuid: null,
          calcom_consultation_uuid: consultation.calcomConsultationUuid,
          xero_consultation_code: consultation.xeroConsultationCode,
          payment_id: consultation.paymentId,
          ConsultationStatus: consultation.consultationStatus,
          ConsultationSubStatus: consultation.consultationSubStatus,
          ConsultationType: {
            type: consultation.consultationType?.type,
            duration_mins: consultation.consultationType?.durationMinutes,
          },
          Doctor: consultation.doctor,
          Nurse: {
            id: consultation.nurse?.id,
            NurseUser: {
              first_name: consultation.nurse?.firstName,
              last_name: consultation.nurse?.lastName,
            },
          },
          waiting_room_status: consultation.waitingRoomStatus,
        });
        setCreatedConsultation(undefined);
        refreshConsultationTab();
      }

      toast.success('Spot consultation created successfully');
    }
  }, [
    createInstantConsultationError,
    consultationData,
    createInstantConsultationSuccess,
    setShowConsultation,
    createdConsultation,
  ]);

  const handleDocNotes = (val: string) => {
    if (!showConsultation) {
      return;
    }

    setShowConsultation((prev: ShowConsultation) => ({ ...prev, notes: val }));
  };
  const { mutateAsync: savePatientTelecomAttributes } = useSavePatientTelecomAttributes(props.patientId);
  const { contactAttributes, contactId } = useAmazonConnectCcp();

  // method creations: manual for version 1 or when auto creation fails; auto for version 2
  const handleSpotConsultationCreation = async (methodCreation?: string) => {
    const res = await createSpotConsultation({ patient_id: props.patientId, methodCreation });

    if (res) {
      if (contactId) {
        TelecomService.setSpotConsultationContactId(contactId, res.data.attributes.id);
        const { immediateNurseVoiceCallAttributesV1, immediateNurseVoiceCallAttributesV2 } =
          triggerSourcePatientTelecomAttributeEnum.enum;
        const triggerSource =
          contactAttributes?.triggerSource?.value === immediateNurseVoiceCallAttributesV2
            ? immediateNurseVoiceCallAttributesV2
            : immediateNurseVoiceCallAttributesV1;

        await savePatientTelecomAttributes(contactId, res.data.attributes.id, triggerSource);
      }

      // On creation of the spot consult save any values added into the patient details form, this also triggers refresh
      setCreatedConsultation(res.data);
      props.onSaveConsultation();
    }
  };

  const handleOpenRunningLate = async () => {
    const modal = showModal(
      ConfirmDialog,
      {
        title: 'Email patient',
        children: 'Send a running late email to the patient?',
        actions: (
          <>
            <Button
              onClick={() => {
                modal.hide();
                handleSendLateMail();
              }}
            >
              Yes
            </Button>
            <Button
              color="primary"
              onClick={() => {
                modal.hide();
              }}
            >
              No
            </Button>
          </>
        ),
      },
      // prevent modal from closing when backdrop is clicked
      { hideOnClose: false },
    );
  };

  const handleOpenSpotConsultation = async () => {
    const modal = showModal(
      ConfirmDialog,
      {
        title: 'Arrange Spot Consultation',
        children: 'This will create a consultation for this patient, right now, are you sure?',
        actions: (
          <>
            <Button
              onClick={() => {
                modal.hide();
                handleSpotConsultationCreation('MANUAL');
              }}
            >
              Yes
            </Button>
            <Button
              color="primary"
              onClick={() => {
                modal.hide();
              }}
            >
              No
            </Button>
          </>
        ),
      },
      // prevent modal from closing when backdrop is clicked
      { hideOnClose: false },
    );
  };

  const fieldsToPrefillBookingForm = {
    name: `${FirstName} ${LastName}`,
    email: email,
    phoneNumber: `${phone || settings.localisation.phoneCountryCodes.australia}`,
  };

  /**
   * Used to either create a new patient note or to view / edit an existing note
   * @param selectedNote
   */
  const handleAddOrViewPatientNote = (
    selectedNote:
      | {
          id: number;
          note: string;
          heading: string;
          CreatedBy: {
            first_name?: string;
            last_name?: string;
          };
          created_by: number;
          strike_reason?: string;
        }
      | undefined,
  ) => {
    // if no selected note passed, then start a new note
    // blank canvas, easiest option for initial refactor
    if (!selectedNote) {
      setSelectedPatientNote({
        //@ts-expect-error: TS2353 - need to fix this type error
        id: null,
        heading: '',
        notes: '',
        createdByName: '',
        createdById: null,
        redFlag: redFlag ?? false,
        redFlagComment: redFlagComment ?? '',
        hasStrike: false,
        strikeReason: null,
      });
      return;
    }

    setSelectedPatientNote({
      //@ts-expect-error: TS2353 - need to fix this type error
      notes: selectedNote.note,
      heading: selectedNote.heading,
      id: selectedNote.id,
      createdByName: `${selectedNote.CreatedBy?.first_name} ${selectedNote.CreatedBy?.last_name}`,
      createdById: selectedNote.created_by,
      redFlag: redFlag ?? false,
      redFlagComment: redFlagComment ?? '',
      hasStrike: Boolean(selectedNote?.strike_reason),
      strikeReason: selectedNote.strike_reason ?? null,
    });
  };

  const handleStatusChange = (statusId: number) => {
    setShowConsultation((prev: ShowConsultation) => ({ ...prev, status_id: statusId }));
  };

  const handleSubStatusChange = (subStatus: ConsultationSubStatusType) => {
    setShowConsultation((prev: ShowConsultation) => ({ ...prev, sub_status_id: subStatus.id }));
    setConsultationSubStatus(subStatus);
  };

  const handleClose = () => {
    if (!ffUnsavedConsultationChanges || !checkHaveNotesChanged()) {
      setShowConsultation(null);
      updatePrescriptionPadStep(PrescriptionPadStep.Create);
      return;
    }

    const modal = showModal(ConfirmDialog, {
      title: 'Unsaved changes',
      children:
        'There are unsaved changes, would you like to keep editing or exit the consultation? Any unsaved changes will be lost.',
      actions: (
        <>
          <Button
            data-testid="unsaved-changes-cancel"
            variant="contained"
            onClick={() => {
              modal.hide();
            }}
          >
            Keep editing
          </Button>
          <Button
            data-testid="unsaved-changes-confirm"
            onClick={() => {
              modal.hide();
              setShowConsultation(null);
              updatePrescriptionPadStep(PrescriptionPadStep.Create);
            }}
          >
            Exit without saving
          </Button>
        </>
      ),
    });
  };

  const overviewTabHeaderSection = (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', lg: 'row' }}
      justifyContent={'space-between'}
      alignItems={'baseline'}
      gap={1}
      marginBottom={4}
    >
      <TextField
        onChange={debounce(handleSearchOnChange, 1000)}
        label="Search"
        variant="outlined"
        size="small"
        sx={{ minWidth: (t) => t.spacing(66), width: { xs: '100%', lg: 'auto' } }}
      />
      <Stack gap={1} sx={{ flexDirection: { sm: 'column', md: 'row' }, alignSelf: 'stretch' }}>
        {!isAdminOnly && (
          <Button
            variant="outlined"
            color="primary"
            size="large"
            disabled={loadingConsultCompletion || !patientActive}
            onClick={handleOpenRunningLate}
            sx={{ width: { sm: '100%', md: 'auto' } }}
          >
            Running late
          </Button>
        )}
        {/* TODO remove ffNurseInstantConsultationCreationEnabled after phase 2 is released to 100% mobile users */}
        {ffNurseInstantConsultationCreationEnabled && (
          <Button
            variant="outlined"
            color="primary"
            size="large"
            disabled={loadingConsultCompletion || !patientActive}
            onClick={handleOpenSpotConsultation}
            sx={{ width: { sm: '100%', md: 'auto' } }}
          >
            Arrange spot consult
          </Button>
        )}
        {Boolean(consultationData.length) && (
          <BookConsultationMenu
            isPatientActive={patientActive}
            isPatientFromTas={isPatientFromTasmania(props.patient?.state_id)}
            fieldsToPrefillBookingForm={fieldsToPrefillBookingForm}
            onBookingSuccessful={props.refresh}
            doctorsList={props.doctorsList}
            doctor={props.doctor}
            refreshConsultationTab={refreshConsultationTab}
            enableBookAnyDoctorButton={enableBookAnyDoctorButton}
            enableBookSpecificDoctorButton={enableBookSpecificDoctorButton}
            initialDoctorConsultCompleted={initialDoctorConsultCompleted}
            initialCompletedNurseConsults={initialCompletedNurseConsults}
          />
        )}
      </Stack>
    </Box>
  );

  const renderFollowUpConsultationSelect = () => {
    if (!(createScriptStep === PrescriptionPadStep.Create)) {
      return null;
    }

    return (
      <Stack width="80%">
        <SelectField
          label="Follow-up Consultation"
          id="prescribed-product-weeks"
          options={[...Array(20).keys()].map((itm) => {
            return { name: `${itm + 1} weeks`, value: itm + 1 };
          })}
          value={followUpConsultationWeeks}
          onChange={(e: {
            target: {
              value: any;
            };
          }) => setFollowUpConsultationWeeks(e.target.value)}
          customGrid={[4, 3]}
          margin={0}
          varient="outlined"
          labelPadding="7px 7px 7px 10px"
          disabled={!patientActive}
        />
      </Stack>
    );
  };

  const isUserAuthorisedForConsultation =
    showConsultation?.doctor_id && (showConsultation?.Doctor?.DoctorUser?.id === user?.id || isAdmin);

  const { showDialog } = useAutoSpotConsultationCreationDialog({
    onCreateSuccess: () => handleSpotConsultationCreation('AUTO'),
    hasError: !!createInstantConsultationError,
  });

  const handleAutoSpotConsultationCreation = async () => {
    if (
      contactAttributes &&
      contactAttributes.triggerSource &&
      contactAttributes.triggerSource.value ===
        triggerSourcePatientTelecomAttributeEnum.enum.immediateNurseVoiceCallAttributesV2 &&
      contactId &&
      !TelecomService.getSpotConsultationContactId(contactId)
    ) {
      await showDialog();
    }
  };

  const debouncedHandleAutoSpotConsultationCreation = useDebouncedCallback(handleAutoSpotConsultationCreation, 100);

  useEffect(() => {
    if (!createdConsultation && ffIsC4CImmediateNurseConsultationV2Enabled) {
      debouncedHandleAutoSpotConsultationCreation();
    }
  }, [createdConsultation, ffIsC4CImmediateNurseConsultationV2Enabled]);

  const updateTrackingContext = useCallback(
    (ctx: TypedAnalyticsContexts) => ({
      ...ctx,
      consultation: { ...ctx.consultation, consultation_id: showConsultation.id.toString() },
    }),
    [showConsultation],
  );

  return (
    <>
      {consultationLinkProps && <ConsultationLinkCard {...consultationLinkProps} />}
      {showConsultation ? (
        <AnalyticsTrackingProvider update={updateTrackingContext}>
          <ShowConsultation
            showConsultation={showConsultation}
            setShowConsultation={setShowConsultation}
            authData={authData}
            handleClose={handleClose}
            patient={props?.patient}
            patientId={String(props.patientId)}
            patientActive={!!patientActive}
            fields={props.fields}
            handleChange={props.handleChange}
            handleDocNotes={handleDocNotes}
            renderFollowUpConsultationSelect={renderFollowUpConsultationSelect}
            isUserAuthorisedForConsultation={isUserAuthorisedForConsultation}
            createScriptStep={createScriptStep}
            doctor={props.doctor}
            isPostingPrescription={isPostingPrescription}
            isPaymentButtonDisabled={isPaymentButtonDisabled}
            statusIsNotPaid={statusIsNotPaid}
            paymentsButtonAction={handleConsultPayment}
            getPaymentButtonText={getPaymentButtonText}
            canShowDiscountRateInput={canShowDiscountRateInput}
            loadingPaymentAction={loadingAnyPayment}
            handleCompleteConsultation={handleCompleteConsultation}
            handleNoShow={onNoShow}
            handleCancelScript={onClearPrescription}
            nurseOnSave={nurseOnSave}
            onSaveConsultation={props.onSaveConsultation}
            isComplimentary={isComplimentary}
            handleIsComplimentaryChange={handleIsComplimentaryChange}
            consultationStatuses={consultationStatuses}
            handleStatusChange={handleStatusChange}
            consultationSubStatus={consultationSubStatus}
            handleSubStatusChange={handleSubStatusChange}
            handleConcessionPayment={handleConcessionPayment}
            consultDiscountRate={consultDiscountRate}
            handleChangeConsultDiscountRate={handleChangeConsultDiscountRate}
            loadingAnyPayment={loadingAnyPayment}
            handleShopifyPaymentRequest={handleShopifyPaymentRequest}
            isSavingConsultation={loadingConsultCompletion}
            saveConsultation={props.saveConsultation}
            prescriptionDetails={props.prescriptionDetails}
            onPrescriptionErrorClosed={props.onPrescriptionErrorClosed}
            prescribeStateError={prescribeStateError}
            onOnlyCancelComplete={onOnlyCancelComplete}
          />
        </AnalyticsTrackingProvider>
      ) : (
        <Box>
          {selectedPatientNote ? (
            <ViewPatientNote
              patientId={props.patientId}
              patientActive={!!patientActive}
              selectedPatientNote={selectedPatientNote}
              refetchNotes={refetchNotes}
              //@ts-expect-error: TS2322 - need to fix this type error
              setSelectedPatientNote={setSelectedPatientNote}
              refetch={props.refresh}
            />
          ) : (
            <Box height="fit-content">
              <SectionHeader headerText="Consultation Overview" />
              <Box paddingY={6} mb={6} data-testid="consultation-table">
                {overviewTabHeaderSection}
                <ConsultationTabOverviewTable
                  consultations={consultationsOverviewTableData}
                  isLoadingConsultations={props.isLoadingConsultations}
                  isErrorConsultations={props.isErrorConsultations}
                  searchFilterText={searchFilterText}
                  handlePaginationChange={props.handlePaginationChange}
                  responseCursors={props.responseCursors}
                  onSort={props.onSort}
                  isProcessingConsultations={isProcessingConsultations}
                  scheduledConsultId={scheduledConsultId}
                />
              </Box>
              <>
                <SectionHeader headerText="Notes" />
                <Box paddingY={6}>
                  <PatientNotes
                    notes={notes}
                    handleNotesPaginationChange={handleNotesPaginationChange}
                    responseNotesCursors={responseNotesCursors}
                    setNotesCursors={setNotesCursors}
                    setNotesSearch={setNotesSearch}
                    onSort={onSort}
                    //@ts-expect-error: TS2322 - need to fix this type error
                    onViewNote={handleAddOrViewPatientNote}
                    //@ts-expect-error: TS2322 - need to fix this type error
                    onAddNewNote={handleAddOrViewPatientNote}
                    isAddNoteDisabled={loadingConsultCompletion || !patientActive}
                    isPatientNotesError={isPatientNotesError}
                  />
                </Box>
              </>
            </Box>
          )}
        </Box>
      )}

      <CompleteConsultationModal
        isOpen={isCompleteConsultationModalOpen}
        onConfirm={handleConfirmCompleteConsult}
        onClose={handleCloseCompleteConsultationModal}
      />
    </>
  );
};

export default ConsultationTab;
